import type { ReactNode } from "react";
import {
  Animated,
  Pressable,
  StyleSheet,
  Text,
  View,
  type GestureResponderHandlers,
  type LayoutChangeEvent,
} from "react-native";
import { useTranslation } from "react-i18next";

import { Icon } from "../Icon/Icon";
import { IconButton } from "../primitives/IconButton";
import { PremiumBadge } from "../primitives/PremiumBadge";
import { helpTextStyle } from "../primitives/helpTextStyle";
import { webTapFloor, type UiTheme } from "../theme";
import { HomeDragHandle } from "./HomeDragHandle";
import type { RowOffset } from "./useSortableDrag";

// HR-11: one row of a widget config sheet (Home, Progress). Three kinds, one box:
//   · pinned: name + "Always shown", no handle, no ✕;
//   · active: drag handle + name + ✕ (hide), or "Always shown" when the
//     widget moves but cannot be hidden (no `onHide`);
//   · inactive: name + ＋ (show);
//   · locked (PG-64, the report's premium sections): name + the Premium
//     badge, no action.
// PG-57: an active row can also carry the collapse toggle (Progress only),
// written as a word (Open / Closed) beside its chevron, never the icon alone.
// The handle (HomeDragHandle) is also the screen-reader and keyboard door for
// reordering, instead of visible arrows.

type Common = { theme: UiTheme; label: string };

type Props = Common &
  (
    | { kind: "pinned" }
    | {
        kind: "active";
        /** Absent: the widget cannot be hidden, so there is no ✕. */
        onHide?: () => void;
        onMoveBy: (delta: -1 | 1) => void;
        canMoveUp: boolean;
        canMoveDown: boolean;
        /** Slot among the movable rows (keeps keyboard focus after a move). */
        position: number;
        handle: GestureResponderHandlers;
        dragging: boolean;
        offset: RowOffset;
        onLayout: (e: LayoutChangeEvent) => void;
        /** Absent: the surface has no collapsed state (Home). */
        collapse?: { collapsed: boolean; onToggle: () => void };
      }
    | { kind: "inactive"; onShow: () => void }
    | { kind: "locked" }
  );

export function WidgetConfigRow(props: Props) {
  const { theme, label } = props;
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  const collapse = props.kind === "active" ? props.collapse : undefined;
  // ▲/▼ and ✕ side by side (Progress): each gives up half the gap on the
  // side it shares, so the two touch zones meet and never overlap on native
  // (TierOneCompareRow's pair). Web drops hitSlop; the 44 floor holds there.
  const { s2, s3 } = theme.spacing;
  const toggleSlop = { top: s3, bottom: s3, left: s3, right: s2 };
  const hideSlop = collapse ? { top: s3, bottom: s3, left: s2, right: s3 } : undefined;
  const name: ReactNode = (
    <Text style={styles.name} selectable={false}>
      {label}
    </Text>
  );
  const alwaysShown = (
    <Text style={styles.pinned} selectable={false}>
      {t("mobile.home.config.pinned", { defaultValue: "Always shown" })}
    </Text>
  );
  let leading: ReactNode = <View style={styles.handle} />;
  let trailing: ReactNode;
  if (props.kind === "pinned") {
    trailing = alwaysShown;
  } else if (props.kind === "locked") {
    // No handle slot: nothing moves here, so the name starts at the edge.
    leading = null;
    // Wrapped: the badge hugs the top of its own box, the row centres the box.
    trailing = (
      <View>
        <PremiumBadge theme={theme} />
      </View>
    );
  } else if (props.kind === "inactive") {
    trailing = (
      <IconButton
        theme={theme}
        name="plus"
        onPress={props.onShow}
        accessibilityLabel={t("mobile.home.config.show", {
          name: label,
          defaultValue: "Show {{name}}",
        })}
      />
    );
  } else {
    leading = (
      <HomeDragHandle
        theme={theme}
        label={label}
        position={props.position}
        canMoveUp={props.canMoveUp}
        canMoveDown={props.canMoveDown}
        onMoveBy={props.onMoveBy}
        handlers={props.handle}
      />
    );
    const hide = props.onHide ? (
      <IconButton
        theme={theme}
        name="close"
        onPress={props.onHide}
        hitSlop={hideSlop}
        accessibilityLabel={t("mobile.home.config.hide", {
          name: label,
          defaultValue: "Hide {{name}}",
        })}
      />
    ) : (
      alwaysShown
    );
    trailing = collapse ? (
      <View style={styles.actions}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            collapse.collapsed
              ? t("mobile.widgetConfig.expand", {
                  name: label,
                  defaultValue: "Expand {{name}}",
                })
              : t("mobile.widgetConfig.collapse", {
                  name: label,
                  defaultValue: "Collapse {{name}}",
                })
          }
          // Kept hand-made (not <Button>/<Chip>): neither carries a disclosure
          // `expanded` state, which is what this control announces.
          // Both props on purpose: RNW drops accessibilityState, the aria
          // prop is what reaches the web DOM.
          accessibilityState={{ expanded: !collapse.collapsed }}
          aria-expanded={!collapse.collapsed}
          onPress={collapse.onToggle}
          hitSlop={toggleSlop}
          style={({ pressed }) => [styles.toggle, pressed && styles.togglePressed]}
        >
          <Text style={styles.toggleText} selectable={false}>
            {collapse.collapsed
              ? t("mobile.widgetConfig.stateClosed", { defaultValue: "Closed" })
              : t("mobile.widgetConfig.stateOpen", { defaultValue: "Open" })}
          </Text>
          <Icon
            name={collapse.collapsed ? "chevron-down" : "chevron-up"}
            size={theme.typography.sizes.base}
            color={theme.palette.ink2}
          />
        </Pressable>
        {hide}
      </View>
    ) : (
      hide
    );
  }

  const box = (
    <View
      style={[
        styles.row,
        props.kind === "active" && props.dragging && styles.rowDragging,
        props.kind === "locked" && styles.rowLocked,
      ]}
    >
      {leading}
      {name}
      {trailing}
    </View>
  );

  if (props.kind === "locked") {
    // One node that says what it is and that it cannot be used (PG-64).
    return (
      <View
        style={styles.slot}
        accessible
        accessibilityLabel={t("mobile.premium.lockedA11y", {
          name: label,
          defaultValue: "{{name}}, Premium, not part of the base report",
        })}
        aria-disabled
      >
        {box}
      </View>
    );
  }
  if (props.kind !== "active") return <View style={styles.slot}>{box}</View>;
  return (
    <Animated.View
      onLayout={props.onLayout}
      style={[styles.slot, props.offset]}
    >
      {box}
    </Animated.View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // The slot carries the gap as padding, not margin, so its measured height
    // is exactly one step of the drag.
    slot: { paddingBottom: s.s3 },
    row: {
      flexDirection: "row",
      alignItems: "center",
      minHeight: 52,
      // A long name wraps (DE at 320px) instead of being cut; the padding
      // keeps a two-line row off the border. 44 + 2 x s2 = the 52 floor, so
      // a one-line row is as tall as before.
      paddingVertical: s.s2,
      // s2, not s3, on the gaps and the right edge: 12px more for the name,
      // so a long German word (Trainingsrhythmus at 320px) fits on its line
      // instead of breaking mid-word. The handle stays 44.
      paddingRight: s.s2,
      gap: s.s2,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
    },
    // Lifted while dragged: stronger edge and a different fill. The row also
    // moves under the finger, so colour is not the only cue.
    rowDragging: { borderColor: p.ink2, backgroundColor: p.surface2 },
    // The locked row has no handle slot: its own inset instead.
    rowLocked: { paddingLeft: s.s4 },
    handle: {
      width: 44,
      height: 44,
      alignItems: "center",
      justifyContent: "center",
    },
    name: { flex: 1, color: p.ink, fontSize: ty.sizes.base },
    // The open/closed control: the word AND the chevron, so the state is
    // written, not left to a glyph that reads as "move down" (#129).
    toggle: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s1,
      minHeight: webTapFloor ?? 36,
      minWidth: webTapFloor,
      paddingHorizontal: s.s2,
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r2,
    },
    togglePressed: { backgroundColor: p.surface2 },
    toggleText: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "600" },
    // s3 between ▲/▼ and ✕: the s2 each gives up of its hitSlop on this
    // side adds up to it, so their touch zones meet without overlapping.
    actions: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    pinned: helpTextStyle(theme),
  });
}
